import {
  faCalendarEdit,
  faCalendarPlus,
} from '@fortawesome/pro-regular-svg-icons';
import React, { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import DateTooltip from 'src/components/ui/DateTooltip/DateTooltip';
import { FormCheckbox } from 'src/components/ui/FormCheckbox/FormCheckbox';
import { FormInput } from 'src/components/ui/FormInput/FormInput';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import { DateFormats } from 'src/constants/dateFormats';
import { space } from 'src/constants/transConstants';
import { addModal, removeModal } from 'src/features/globalSlice';
import { routes } from 'src/routes';
import { resolveTicket } from 'src/store/actions/ticketActions';
import { ModalType } from 'src/store/models/Modal';
import { TicketRepairMethod, TicketStatus } from 'src/store/models/Ticket';
import { formatDate } from 'src/utils/dateFormatter';

import classes from './ResolveTicket.module.scss';

export interface ResolveTicketFormData {
  repairMethod: string[];
  notes: string;
  checkUpDate: string;
}

const defaultValues: ResolveTicketFormData = {
  repairMethod: [],
  notes: '',
  checkUpDate: '',
};

const ResolveTicket: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const ticket = useAppSelector((state) => state.ticket.ticketDetails);

  useEffect(() => {
    if (!ticket || ticket?.status !== TicketStatus.IN_PROGRESS) {
      navigate(routes.TICKETS, { replace: true });
    }
    // eslint-disable-next-line
  }, [ticket]);

  const { control, handleSubmit, watch, setValue, formState } =
    useForm<ResolveTicketFormData>({
      mode: 'onChange',
      defaultValues,
    });
  const repairMethod = watch('repairMethod');
  const checkUpDate = watch('checkUpDate');

  const isValid = !!(repairMethod?.[0] || repairMethod?.[1]);

  const onSubmit: SubmitHandler<ResolveTicketFormData> = (formData) => {
    dispatch(
      addModal({
        modalType: ModalType.WARNING,
        title: t('common:are_you_sure'),
        body: t('ticket_details:ticket_will_be_resolved', {
          ticket: ticket?.ticketId,
        }),
        onBackdropClose: false,
        loadingAction: resolveTicket.toString(),
        secondaryAction: () => dispatch(removeModal()),
        secondaryButtonText: t('common:go_back'),
        primaryAction: () => {
          dispatch(
            resolveTicket({ ticketId: ticket?.id, resolveData: formData })
          );
        },
        primaryButtonText: t('common:confirm'),
      })
    );
  };

  const headerRight = (
    <div className={classes.HeaderRight}>
      <DateTooltip
        tooltipTitle={t('tickets:assign_date')}
        icon={faCalendarPlus}
        date={ticket?.assignedAt}
      />
      <DateTooltip
        tooltipTitle={t('tickets:start_date')}
        icon={faCalendarEdit}
        date={ticket?.startedAt}
      />
      <Button
        type="submit"
        form="resolve-ticket-form"
        className={classes.HeaderButton}
        disabled={!isValid}
      >
        {t('tickets:resolve_ticket')}
      </Button>
    </div>
  );

  return (
    <div className={classes.Container}>
      <PageHeader
        title={ticket ? `${t('common:ticket')} ${ticket?.ticketId}` : space}
        showBackButton
        backConfirmationText={
          formState?.isDirty && t('ticket_details:unsaved_resolve')
        }
        endNode={headerRight}
      />
      <form
        noValidate
        id="resolve-ticket-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <h4>
          {`${t(`ticket_details:repair_method`)} `}
          <span>{`(${t('common:required').toLowerCase()})`}</span>
        </h4>
        <section>
          <FormCheckbox<ResolveTicketFormData>
            name="repairMethod.0"
            control={control}
            LabelCheckboxProps={{
              color: 'primary',
              label: t('ticket_details:remote'),
            }}
            CheckboxProps={{ value: TicketRepairMethod.REMOTE }}
          />
          <FormCheckbox<ResolveTicketFormData>
            name="repairMethod.1"
            control={control}
            LabelCheckboxProps={{
              color: 'primary',
              label: t('ticket_details:in_person'),
            }}
            CheckboxProps={{ value: TicketRepairMethod.IN_PERSON }}
          />
        </section>
        <h4>{t('ticket_details:ticket_notes')}</h4>
        <section>
          <FormInput<ResolveTicketFormData>
            control={control}
            name="notes"
            placeholder={t('tickets:make_a_note')}
            inputProps={{
              fullWidth: true,
              multiline: true,
              rows: 4,
            }}
          />
        </section>
        <h4>{t('ticket_details:plan_maintenance')}</h4>
        <section>
          <p>{t('ticket_details:suggest_maintenance')}</p>
          <FormInput<ResolveTicketFormData>
            control={control}
            name="checkUpDate"
            type="date"
            inputProps={{
              inputProps: {
                min: formatDate(Date().toString(), DateFormats.INPUT_DATE),
              },
            }}
            placeholder={t('common:choose_date')}
          />
          {checkUpDate ? (
            <p onClick={() => setValue('checkUpDate', '')}>
              {t('common:clear_field')}
            </p>
          ) : null}
        </section>
      </form>
    </div>
  );
};

export default ResolveTicket;
